import { useMemo } from "react";
import { useParams } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import Typography from "core/components/Typography";
import { ROLES } from "core/constants";
import useActiveMembership from "core/hooks/useActiveMembership";
import useAuthenticatedMutation from "core/hooks/useAuthenticatedMutation";
import { useOrganizationByIdQuery } from "core/queries";
import DetailsViewer from "modules/admin/DetailsViewer";
import OrgMemberLicenseForm from "modules/admin/OrgMemberLicenseForm";
import { updateOrganizationLicenses } from "modules/admin/actions";
import { toast } from "sonner";

const OrganizationMemberLicensesPage = () => {
  const { orgId: organizationId } = useParams();
  const queryClient = useQueryClient();
  const { activeMembership } = useActiveMembership();

  const { data: organization, isLoading } = useOrganizationByIdQuery({
    organizationId,
    include: "licenses",
  });

  const memberLicense = useMemo(
    () => ({
      id: organization?.id,
      licenses: {
        user_limit: organization?.licenses?.user_limit,
        tableau_user_licenses:
          organization?.licenses?.tableau_user_licenses || {},
        analytics_hub_user_licenses:
          organization?.licenses?.analytics_hub_user_licenses || {},
      },
    }),
    [organization]
  );

  const updateLicenses = useAuthenticatedMutation((req) =>
    updateOrganizationLicenses({ ...req, organizationId })
  );

  const licenseMutation = useMutation({
    mutationFn: updateLicenses,
    onSuccess: (_, updatedLicenses) => {
      queryClient.setQueryData(
        ["organizations", { organizationId, include: "licenses" }],
        (cachedOrg) => {
          if (!cachedOrg) return cachedOrg;
          return {
            ...cachedOrg,
            ...updatedLicenses,
            isUpdated: true,
            resetUpdateParams: {
              queryKey: [
                "organizations",
                { organizationId, include: "licenses" },
              ],
              rowId: organizationId,
            },
          };
        }
      );

      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      toast.success("Member licenses have been updated.");
    },
    onError: () => {
      toast.error("Failed to update member licenses.");
    },
  });

  const canEdit = activeMembership.role === ROLES.systemAdmin;

  return (
    <div style={{ maxWidth: "500px" }}>
      <Typography variant="h2" noMargin style={{ marginBottom: "5px" }}>
        Member Licenses
      </Typography>

      {canEdit ? (
        <OrgMemberLicenseForm
          onSubmit={licenseMutation.mutate}
          defaultValues={memberLicense}
          isFetching={isLoading}
          isSubmitting={licenseMutation.isPending}
          error={licenseMutation.error}
          submitLabel="Save Changes"
          disabledUntilTouched
        />
      ) : (
        <DetailsViewer
          isLoading={isLoading}
          data={[
            {
              label: "Member Limit",
              value: organization?.licenses?.user_limit,
            },
            {
              label: "Analytics Hub Pro Limit",
              value:
                organization?.licenses?.analytics_hub_user_licenses?.pro_count,
            },
            {
              label: "BI Portal Explorer",
              value:
                organization?.licenses?.tableau_user_licenses?.explorer_count,
            },
            {
              label: "BI Portal Viewer",
              value:
                organization?.licenses?.tableau_user_licenses?.viewer_count,
            },
            {
              label: "BI Portal Creator",
              value:
                organization?.licenses?.tableau_user_licenses?.creator_count,
            },
          ]}
        />
      )}
    </div>
  );
};

export default OrganizationMemberLicensesPage;
